import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, ExternalLink, Crown, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const TIER_LABELS = { premium: "Premium", featured: "Featured", standard: "Promoted" };

export default function PromotionsPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/sponsored-videos`).then((r) => setVideos(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="promotions-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" className="mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Partner Showcase</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">
              Sponsored<br /><span className="gold-gradient-text">Content</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/60 font-body font-light text-base leading-relaxed max-w-2xl">
              Discover exceptional events and services from our trusted partners. Each featured partner has been vetted to meet the Aristocrats JA standard of excellence.
            </motion.p>
          </motion.div>

          {loading ? (
            <div className="text-gold font-body text-sm animate-pulse">Loading promotions...</div>
          ) : videos.length === 0 ? (
            <motion.div initial="hidden" animate="visible" className="glass-card p-16 text-center">
              <motion.div variants={fadeUp} custom={0}>
                <Sparkles size={48} className="text-gold/30 mx-auto mb-4" />
                <h3 className="text-white font-heading text-2xl mb-3">Partner Spotlight Coming Soon</h3>
                <p className="text-white/40 font-body text-sm mb-8">Interested in featuring your brand here?</p>
                <Link to="/partner" data-testid="become-partner-cta" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors">
                  Become a Partner
                </Link>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video, i) => (
                <motion.div key={video.id} variants={fadeUp} custom={i} className="glass-card overflow-hidden group" data-testid={`sponsored-video-${video.id}`}>
                  {/* Video Thumbnail / Embed */}
                  <div className="relative aspect-video bg-luxury-surface">
                    {video.video_url.includes("youtube") || video.video_url.includes("youtu.be") ? (
                      <iframe
                        src={video.video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/")}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                        allowFullScreen
                        title={video.title}
                      />
                    ) : video.video_url.includes("vimeo") ? (
                      <iframe
                        src={video.video_url.replace("vimeo.com/", "player.vimeo.com/video/")}
                        className="w-full h-full"
                        allow="autoplay; fullscreen"
                        allowFullScreen
                        title={video.title}
                      />
                    ) : (
                      <video src={video.video_url} controls className="w-full h-full object-cover" poster={video.thumbnail_url || undefined} />
                    )}
                    {/* Tier Badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`px-3 py-1 text-[10px] font-body font-medium uppercase tracking-wider ${
                        video.tier === "premium" ? "bg-gold text-black" :
                        video.tier === "featured" ? "bg-gold/20 text-gold border border-gold/40" :
                        "bg-white/10 text-white/60"
                      }`}>
                        {TIER_LABELS[video.tier] || "Promoted"}
                      </span>
                    </div>
                  </div>
                  {/* Info */}
                  <div className="p-6">
                    <p className="text-gold text-xs font-body uppercase tracking-wider mb-2">{video.company_name}</p>
                    <h3 className="text-white font-heading text-lg mb-2">{video.title}</h3>
                    {video.description && <p className="text-white/50 font-body font-light text-xs leading-relaxed">{video.description}</p>}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Partner CTA */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-20 text-center">
            <motion.div variants={fadeUp} custom={0} className="glass-card p-12 max-w-2xl mx-auto">
              <Crown size={32} className="text-gold mx-auto mb-4" />
              <h3 className="text-white font-heading text-2xl mb-3">Feature Your Brand</h3>
              <p className="text-white/50 font-body text-sm mb-6">Get your event or service in front of our exclusive VIP audience. Affordable tiers starting from $250/week.</p>
              <Link to="/partner" data-testid="partner-cta-bottom" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors">
                Get Started
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
